import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabaseClient } from '../../lib/supabase';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { logger } from '../../lib/logger';
import type { ReviewGuest } from '../../types/review';

interface ReviewClientProps {
  config: any;
  translations: Record<string, Record<string, string>>;
}

export class ReviewClient {
  private config: any;
  private translations: Record<string, Record<string, string>>;
  private isLoading: boolean = false;
  private error: string | null = null;

  constructor({ config, translations }: ReviewClientProps) {
    this.config = config;
    this.translations = translations;
  }

  public async sendEmailNotification(review: ReviewGuest): Promise<boolean> {
    try {
      this.isLoading = true;
      this.error = null;

      // Get hotel notification email
      const { data: hotel, error: hotelError } = await supabaseClient
        .from('hotels')
        .select('name, notification_email, defaultlanguage')
        .eq('id', HOTEL_ID)
        .single();

      if (hotelError) {
        throw hotelError;
      }

      if (!hotel.notification_email) {
        logger.warn('No notification email configured for hotel', {
          category: 'Review',
          data: { hotelId: HOTEL_ID }
        });
        return false;
      }

      // Get section name if section_id is provided
      let sectionName = '';
      if (review.section_id) {
        const { data: section, error: sectionError } = await supabaseClient
          .from('hotels_navigation')
          .select('name_fr')
          .eq('id', review.section_id)
          .single();

        if (!sectionError && section) {
          sectionName = section.name_fr;
        }
      }

      // Format the rating as stars
      const ratingStars = '⭐'.repeat(review.rating);

      // Get translations for email
      const emailSubject = this.translations.email_subject?.[hotel.defaultlanguage] || 'Nouvel avis - {rating} {platform}';
      const formattedSubject = emailSubject
        .replace('{rating}', ratingStars)
        .replace('{hotel}', hotel.name)
        .replace('{platform}', review.platform);

      // Log the request details for debugging
      logger.debug('Sending review notification', {
        category: 'Review',
        data: {
          to: hotel.notification_email,
          subject: formattedSubject,
          reviewId: review.id,
          platform: review.platform,
          rating: review.rating,
          supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
          anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'present' : 'missing'
        }
      });

      // Call the Edge Function to send the email
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/review-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: hotel.notification_email,
          subject: formattedSubject,
          html: this.generateEmailHtml(review, hotel.name, sectionName, this.translations, hotel.defaultlanguage),
          text: this.generateEmailText(review, hotel.name, sectionName, this.translations, hotel.defaultlanguage)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send notification');
      }

      logger.debug('Review notification sent successfully', {
        category: 'Review',
        data: {
          reviewId: review.id,
          platform: review.platform,
          rating: review.rating,
          email: hotel.notification_email
        }
      });

      return true;
    } catch (error) {
      logger.error('Error sending review notification', {
        category: 'Review',
        data: { error }
      });
      this.error = error instanceof Error ? error.message : 'Unknown error';
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  // Generate HTML email content
  private generateEmailHtml(
    review: ReviewGuest,
    hotelName: string,
    sectionName: string,
    translations: Record<string, Record<string, string>>,
    language: string = 'fr'
  ): string {
    const getTranslation = (key: string): string => {
      return translations[key]?.[language] || translations[key]?.fr || key;
    };

    const formatDate = (dateStr: string): string => {
      try {
        const date = new Date(dateStr);
        return date.toLocaleString(language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateStr;
      }
    };

    // Format the rating as stars
    const ratingStars = '⭐'.repeat(review.rating);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
          }
          .container {
            padding: 20px;
            border: 1px solid #ddd;
            border-radius: 5px;
          }
          .header {
            background-color: ${this.config['major-color']};
            color: white;
            padding: 15px;
            border-radius: 5px 5px 0 0;
            margin: -20px -20px 20px;
          }
          .rating {
            font-size: 24px;
            margin: 10px 0;
          }
          .info-row {
            margin-bottom: 10px;
          }
          .label {
            font-weight: bold;
            margin-right: 5px;
          }
          .comment {
            background-color: #f9f9f9;
            padding: 15px;
            border-radius: 5px;
            margin-top: 15px;
            border-left: 4px solid ${this.config['major-color']};
          }
          .footer {
            margin-top: 20px;
            font-size: 12px;
            color: #777;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>${getTranslation('email_title')}</h1>
          </div>
          
          <div class="rating">${ratingStars}</div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_hotel')}:</span>
            <span>${hotelName}</span>
          </div>
          
          ${sectionName ? `
          <div class="info-row">
            <span class="label">${getTranslation('email_section')}:</span>
            <span>${sectionName}</span>
          </div>
          ` : ''}
          
          <div class="info-row">
            <span class="label">${getTranslation('email_platform')}:</span>
            <span>${review.platform}</span>
          </div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_date')}:</span>
            <span>${formatDate(review.created_at || new Date().toISOString())}</span>
          </div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_language')}:</span>
            <span>${review.language}</span>
          </div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_client')}:</span>
            <span>${review.name}</span>
          </div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_rating')}:</span>
            <span>${ratingStars} (${review.rating}/5)</span>
          </div>
          
          <div class="info-row">
            <span class="label">${getTranslation('email_comment')}:</span>
            <div class="comment">${review.comment.replace(/\n/g, '<br>')}</div>
          </div>
          
          <div class="footer">
            ${getTranslation('email_footer')}
          </div>
        </div>
      </body>
      </html>
    `;
  }

  // Generate plain text email content
  private generateEmailText(
    review: ReviewGuest,
    hotelName: string,
    sectionName: string,
    translations: Record<string, Record<string, string>>,
    language: string = 'fr'
  ): string {
    const getTranslation = (key: string): string => {
      return translations[key]?.[language] || translations[key]?.fr || key;
    };

    const formatDate = (dateStr: string): string => {
      try {
        const date = new Date(dateStr);
        return date.toLocaleString(language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateStr;
      }
    };

    // Format the rating as stars
    const ratingStars = '⭐'.repeat(review.rating);

    return `
${getTranslation('email_title')}

${ratingStars}

${getTranslation('email_hotel')}: ${hotelName}
${sectionName ? `${getTranslation('email_section')}: ${sectionName}\n` : ''}
${getTranslation('email_platform')}: ${review.platform}
${getTranslation('email_date')}: ${formatDate(review.created_at || new Date().toISOString())}
${getTranslation('email_language')}: ${review.language}
${getTranslation('email_client')}: ${review.name}
${getTranslation('email_rating')}: ${ratingStars} (${review.rating}/5)

${getTranslation('email_comment')}:
${review.comment}

---
${getTranslation('email_footer')}
    `.trim();
  }

  public submitReview(review: ReviewGuest): Promise<boolean> {
    return reviewClient.submitReview(review);
  }
}

export default ReviewClient;